import { useCallback, useState } from 'react'
import { ArrowLeft, Check, Copy, Download } from 'lucide-react'
import MarkdownMessage from '../MarkdownMessage'

type Props = {
  title: string
  content: string
  onBack: () => void
}

export default function FlowDeliverableReader({ title, content, onBack }: Props) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* ignore */
    }
  }, [content])

  const handleDownload = useCallback(() => {
    const slug = title.replace(/[^\w\-]+/g, '-').toLowerCase().slice(0, 60)
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${slug || 'livrable'}.md`
    a.click()
    URL.revokeObjectURL(url)
  }, [content, title])

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-emerald-100/80 px-4 py-3 dark:border-emerald-900/40">
        <button
          type="button"
          onClick={onBack}
          className="rounded-full p-2 text-zinc-500 transition hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
          aria-label="Retour à la liste"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-zinc-900 dark:text-white">
          {title}
        </h3>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1 rounded-full border border-emerald-700/50 bg-emerald-950/30 px-3 py-1.5 text-xs font-medium text-emerald-200 transition hover:bg-emerald-900/40"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copié' : 'Copier tout'}
        </button>
        <button
          type="button"
          onClick={handleDownload}
          className="rounded-full p-2 text-zinc-500 transition hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
          title="Télécharger .md"
        >
          <Download className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <MarkdownMessage content={content} className="text-sm leading-relaxed text-zinc-800 dark:text-zinc-100" />
      </div>
    </div>
  )
}
